<script setup lang="ts">
  import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue'
  import { Button } from '@/components/ui/button'
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
  } from '@/components/ui/command'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    XMarkIcon
  } from '@heroicons/vue/24/outline'
  import { CheckIcon, Calendar as CalendarIcon } from 'lucide-vue-next'
  import { computed, onMounted, onUnmounted, ref, useSlots, watch } from 'vue'
  import { useRoute, useRouter, type LocationQuery } from 'vue-router'
  import { parsePositiveInteger } from '@/utils/numberUtils'
  import CheckboxField from './form/CheckboxField.vue'
  import { RangeCalendar } from '@/components/ui/range-calendar'
  import { TimePicker } from '@/components/ui/time-picker'
  import type { DateRange } from 'radix-vue'
  import { CalendarDate as CalendarDateCtor } from '@internationalized/date'

  export interface Filter {
    key: string
    label: string
    type: 'boolean' | 'number' | 'string' | 'select' | 'multi-select' | 'date-range'
    value?: any
    options?: { label: string; value: any }[]
  }

  export interface FetchDataOptions {
    [key: string]: any

    limit?: number
    offset?: number
    search?: string
    filters?: Filter[]
  }

  export interface PaginatedResponse<T> {
    results: T[]
    next: string | null
    previous: string | null
    count: number
  }

  const props = withDefaults(
    defineProps<{
      fetchData: (options?: FetchDataOptions) => Promise<PaginatedResponse<any>>
      fetchOptions?: FetchDataOptions
      searchableFields?: string
      filterableFields?: Filter[]
      searchDebounce?: number
      selectable?: boolean
      selectedItems?: any[]
      /** When true, shows very subtle vertical column dividers */
      showColumnDividers?: boolean
      /** Initial page to load on mount */
      initialPage?: number
      /** data-cy attribute for pagination nav container */
      paginationNavDataCy?: string
      /** data-cy attribute for pagination prev button */
      paginationPrevDataCy?: string
      /** data-cy attribute for pagination next button */
      paginationNextDataCy?: string
      /** data-cy attribute for pagination input */
      paginationInputDataCy?: string
      /** data-cy attribute for pagination results count */
      paginationResultsCountDataCy?: string
    }>(),
    {
      initialPage: 1,
      paginationNavDataCy: undefined,
      paginationPrevDataCy: undefined,
      paginationNextDataCy: undefined,
      paginationInputDataCy: undefined,
      paginationResultsCountDataCy: undefined,
      showColumnDividers: true
    }
  )

  const slots = useSlots()
  const { t } = useTranslation({
    en: {
      Results: 'results',
      NoResultsFound: 'No results found',
      NoResultsFoundDescription:
        "Try adjusting your search or filter to find what you're looking for.",
      Of: 'of',
      Previous: 'Previous',
      Next: 'Next',
      Search: 'Search',
      ResetFilters: 'Reset Filters',
      Yes: 'Yes',
      No: 'No',
      SelectAll: 'Select All',
      SearchOptions: 'Search options...',
      NoOptionsFound: 'No options found.'
    },
    de: {
      Results: 'Einträge',
      NoResultsFound: 'Keine Einträge gefunden',
      NoResultsFoundDescription:
        'Versuchen Sie, Ihre Suche oder die Filterung anzupassen, um das Gesuchte zu finden.',
      Of: 'von',
      Previous: 'Zurück',
      Next: 'Weiter',
      Search: 'Suche nach',
      ResetFilters: 'Filter zurücksetzen',
      Yes: 'Ja',
      No: 'Nein',
      SelectAll: 'Alle auswählen',
      SearchOptions: 'Optionen suchen...',
      NoOptionsFound: 'Keine Optionen gefunden.'
    },
    fr: {
      Results: 'résultats',
      NoResultsFound: 'Aucun résultat trouvé',
      NoResultsFoundDescription:
        "Essayez d'ajuster votre recherche ou vos filtres pour trouver ce que vous cherchez.",
      Of: 'de',
      Previous: 'Précédent',
      Next: 'Suivant',
      Search: 'Rechercher',
      ResetFilters: 'Réinitialiser les filtres',
      Yes: 'Oui',
      No: 'Non',
      SelectAll: 'Tout sélectionner',
      SearchOptions: 'Rechercher des options...',
      NoOptionsFound: 'Aucune option trouvée.'
    }
  })

  const emit = defineEmits<{
    'update:selectedItems': [items: any[]]
    'page-change': [page: number]
  }>()

  const columnCount = computed(() => {
    const headSlot = slots['table-head']
    if (headSlot) {
      const rendered = headSlot({})
      if (Array.isArray(rendered) && rendered.length > 0) {
        // Filter out any undefined or null elements and get real length
        const validElements = rendered.filter((el) => el != null)
        return validElements.length || 3
      }
    }
    return 3
  })

  const searchTerm = ref('')
  const filterSearchTerm = ref('')
  const paginatedData = ref<PaginatedResponse<any> | null>(null)
  const currentPage = ref(props.initialPage)
  const inputPage = ref(props.initialPage.toString())
  const limit = computed(() => props.fetchOptions?.limit || 20)
  const filters = ref<Filter[]>([])
  const isLoading = ref(false)
  const isInitializing = ref(true)
  const selectedItems = ref<any[]>(props.selectedItems || [])
  const selectAll = ref(false)
  const userModifiedFilters = ref<Set<string>>(new Set())
  const restoringFromQuery = ref(false)
  const route = useRoute()
  const router = useRouter()
  let latestRequestId = 0

  const totalPages = computed(() => {
    const count = paginatedData?.value?.count ?? 0
    return Math.ceil(count / limit.value)
  })

  const items = computed<any[]>(() => paginatedData.value?.results ?? [])
  const hasNext = computed(() => paginatedData.value?.next !== null)

  const fetchData = async (page: number) => {
    try {
      // Generate unique request ID for this request
      const requestId = ++latestRequestId

      const offset = (page - 1) * limit.value
      currentPage.value = page
      inputPage.value = page.toString()

      const response = await props.fetchData({
        limit: limit.value,
        offset,
        search: searchTerm.value,
        filters: filters.value,
        ...props.fetchOptions
      })

      // Only update data if this is still the latest request
      if (requestId === latestRequestId) {
        paginatedData.value = response
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      throw error
    }
  }

  const goToPage = () => {
    const pageNumber = parsePositiveInteger(inputPage.value)
    if (pageNumber != null && pageNumber <= totalPages.value) {
      handlePageChange(pageNumber)
    } else {
      inputPage.value = currentPage.value.toString()
    }
  }

  let searchDebounceTimeout: ReturnType<typeof setTimeout> | null = null

  const handleSearch = (event: Event) => {
    const value = (event.target as HTMLInputElement).value
    searchTerm.value = value

    const debounceTime = props.searchDebounce || 300

    if (searchDebounceTimeout) {
      clearTimeout(searchDebounceTimeout)
    }

    searchDebounceTimeout = setTimeout(async () => {
      currentPage.value = 1
      emit('page-change', 1)
      await fetchData(currentPage.value)
    }, debounceTime)
  }

  const handlePageChange = async (page: number) => {
    isLoading.value = true
    emit('page-change', page)
    await fetchData(page).finally(() => {
      isLoading.value = false
    })
  }

  const refreshData = async () => {
    isLoading.value = true
    await fetchData(currentPage.value).finally(() => {
      isLoading.value = false
    })
  }

  const resetAndRefresh = async () => {
    isLoading.value = true
    currentPage.value = 1
    inputPage.value = '1'
    emit('page-change', 1)
    await fetchData(1).finally(() => {
      isLoading.value = false
    })
  }

  const toggleSelectAll = () => {
    selectAll.value = !selectAll.value
    if (selectAll.value) {
      selectedItems.value = [...items.value]
    } else {
      selectedItems.value = []
    }
    emit('update:selectedItems', selectedItems.value)
  }

  const toggleSelectItem = (item: any) => {
    const index = selectedItems.value.findIndex((selected) => selected.uuid === item.uuid)
    if (index > -1) {
      selectedItems.value.splice(index, 1)
    } else {
      selectedItems.value.push(item)
    }
    selectAll.value = selectedItems.value.length === items.value.length
    emit('update:selectedItems', selectedItems.value)
  }

  const isItemSelected = (item: any) => {
    return selectedItems.value.some((selected) => selected.uuid === item.uuid)
  }

  const updateItem = (identifier: string | ((item: any) => boolean), updates: Partial<any>) => {
    if (!paginatedData.value?.results) return false

    const index = paginatedData.value.results.findIndex((item) =>
      typeof identifier === 'function' ? identifier(item) : item.uuid === identifier
    )

    if (index !== -1) {
      paginatedData.value.results[index] = {
        ...paginatedData.value.results[index],
        ...updates
      }
      return true
    }
    return false
  }

  defineExpose({
    refreshData,
    resetAndRefresh,
    updateItem,
    loading: isLoading
  })

  const getFilterValue = (key: string) => {
    const filter = filters.value.find((filter: Filter) => filter.key === key)
    return filter?.value
  }

  const getFilterDisplayValue = (key: string) => {
    const filter = filters.value.find((filter: Filter) => filter.key === key)
    if (!filter) return ''

    // For select and multi-select filters, show the label instead of the value
    if ((filter.type === 'select' || filter.type === 'multi-select') && filter.options) {
      if (filter.type === 'select') {
        // Single select: find the matching option and return its label
        const option = filter.options.find((opt: any) => opt.value === filter.value)
        return option?.label || filter.value
      } else {
        // Multi-select: show comma-separated labels
        const values = filter.value ? filter.value.split(',') : []
        const labels = values.map((value: string) => {
          const option = filter.options?.find((opt: any) => opt.value === value)
          return option?.label || value
        })
        return labels.join(', ')
      }
    }

    // For date-range filters, show formatted range (with optional time)
    if (filter.type === 'date-range' && filter.value) {
      const parts = filter.value.split(',')
      if (parts.length === 2) {
        const fmtDate = (s: string) => {
          const datePart = s.trim().split(' ')[0]
          const [y, m, d] = datePart.split('-')
          return `${d}.${m}.${y}`
        }
        const getTime = (s: string) => {
          const pieces = s.trim().split(' ')
          return pieces.length > 1 ? pieces[1] : undefined
        }
        const startDateStr = fmtDate(parts[0])
        const endDateStr = fmtDate(parts[1])
        const startTime = getTime(parts[0])
        const endTime = getTime(parts[1])
        const hasNonDefaultTime =
          (startTime && startTime !== '00:00') || (endTime && endTime !== '23:59')
        if (startDateStr === endDateStr) {
          if (hasNonDefaultTime)
            return `${startDateStr} ${startTime || '00:00'} – ${endTime || '23:59'}`
          return startDateStr
        }
        if (hasNonDefaultTime)
          return `${startDateStr} ${startTime || '00:00'} – ${endDateStr} ${endTime || '23:59'}`
        return `${startDateStr} – ${endDateStr}`
      }
    }

    // For boolean filters, show translated values
    if (filter.type === 'boolean') {
      return filter.value ? t('Yes') : t('No')
    }

    // For other filter types (number, string), show the value as is
    return filter.value
  }

  const removeFilterValue = async (key: string) => {
    const originalFilter = props.filterableFields?.find((filter) => filter.key === key)
    const filter = filters.value.find((filter) => filter.key === key)
    if (filter) {
      filter.value = originalFilter?.value
      userModifiedFilters.value.delete(key)
    }
  }

  const isFilterValueDifferentFromDefault = (filter: Filter) => {
    const originalFilter = props.filterableFields?.find((f) => f.key === filter.key)
    if (!originalFilter) return false

    if (userModifiedFilters.value.has(filter.key)) {
      return true
    }

    const currentValue = filter.value
    const defaultValue = originalFilter.value

    if (filter.type === 'boolean') {
      return currentValue !== defaultValue
    }

    if (filter.type === 'multi-select') {
      const currentValues = currentValue ? currentValue.split(',').sort() : []
      const defaultValues = defaultValue ? defaultValue.split(',').sort() : []
      return JSON.stringify(currentValues) !== JSON.stringify(defaultValues)
    }

    return currentValue !== defaultValue
  }

  // --- Date Range helpers ---
  const dateRangeTimes = ref<Record<string, { startTime: string; endTime: string }>>({})

  const fmtCalendarDate = (d: any) =>
    `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`

  const parseDateRangeValue = (value: string | undefined): DateRange => {
    if (!value) return { start: undefined, end: undefined }
    const parts = value.split(',')
    if (parts.length !== 2) return { start: undefined, end: undefined }
    const parse = (s: string) => {
      const datePart = s.trim().split(' ')[0]
      const [y, m, d] = datePart.split('-').map(Number)
      return y && m && d ? new CalendarDateCtor(y, m, d) : undefined
    }
    return { start: parse(parts[0]), end: parse(parts[1]) }
  }

  const updateDateRangeFilter = (key: string, range: DateRange) => {
    const filter = filters.value.find((f: Filter) => f.key === key)
    if (!filter) return
    if (!range.start || !range.end) return
    if (!dateRangeTimes.value[key]) {
      dateRangeTimes.value[key] = { startTime: '00:00', endTime: '23:59' }
    }
    const times = dateRangeTimes.value[key]
    const formatted = `${fmtCalendarDate(range.start)} ${times.startTime},${fmtCalendarDate(range.end)} ${times.endTime}`
    userModifiedFilters.value.add(key)
    filter.value = formatted
  }

  const setTodayRange = (key: string) => {
    const now = new Date()
    const today = new CalendarDateCtor(now.getFullYear(), now.getMonth() + 1, now.getDate())
    updateDateRangeFilter(key, { start: today, end: today })
  }

  const setYesterdayRange = (key: string) => {
    const d = new Date()
    d.setDate(d.getDate() - 1)
    const yesterday = new CalendarDateCtor(d.getFullYear(), d.getMonth() + 1, d.getDate())
    updateDateRangeFilter(key, { start: yesterday, end: yesterday })
  }

  const updateDateRangeTime = (key: string, field: 'startTime' | 'endTime', time: string) => {
    if (!dateRangeTimes.value[key]) {
      dateRangeTimes.value[key] = { startTime: '00:00', endTime: '23:59' }
    }
    dateRangeTimes.value[key][field] = time
    const filter = filters.value.find((f: Filter) => f.key === key)
    if (!filter?.value) return
    const range = parseDateRangeValue(filter.value)
    if (!range.start || !range.end) return
    const times = dateRangeTimes.value[key]
    filter.value = `${fmtCalendarDate(range.start)} ${times.startTime},${fmtCalendarDate(range.end)} ${times.endTime}`
  }

  const initFilters = () => {
    if (!props.filterableFields) return
    userModifiedFilters.value.clear()
    props.filterableFields.forEach((filter) => {
      filters.value.push({
        key: filter.key,
        value: filter.value,
        label: filter.label,
        type: filter.type,
        options: filter.options
      })
    })
  }

  const stateToQuery = (): Record<string, string> => {
    const q: Record<string, string> = {}
    if (currentPage.value > 1) q['page'] = String(currentPage.value)
    if (searchTerm.value) q['search'] = searchTerm.value
    for (const filter of filters.value) {
      const def = props.filterableFields?.find((f) => f.key === filter.key)
      const defaultVal = def?.value
      const currentVal = filter.value
      const isEmpty = currentVal == null || currentVal === ''
      const isDefault =
        currentVal === defaultVal ||
        (isEmpty && (defaultVal == null || defaultVal === undefined || defaultVal === ''))
      if (!isDefault && !isEmpty) {
        const val = typeof currentVal === 'boolean' ? (currentVal ? '1' : '0') : String(currentVal)
        q[`f_${filter.key}`] = val
      }
    }
    return q
  }

  const applyQueryToState = (query: LocationQuery) => {
    const rawPage = String(query['page'] ?? '1').trim()
    const parsed = parsePositiveInteger(rawPage)
    const page = parsed ?? 1
    const pageToUse =
      totalPages.value >= 1 ? Math.min(Math.max(1, page), totalPages.value) : Math.max(1, page)
    currentPage.value = pageToUse
    inputPage.value = String(pageToUse)
    const searchFromQuery =
      query['search'] != null && query['search'] !== ''
        ? Array.isArray(query['search'])
          ? (query['search'][0] ?? '')
          : query['search']
        : ''
    if (searchTerm.value !== searchFromQuery) {
      searchTerm.value = searchFromQuery
    }

    for (const filter of filters.value) {
      const param = query[`f_${filter.key}`]
      if (param == null) {
        const def = props.filterableFields?.find((f) => f.key === filter.key)
        filter.value = def?.value
        userModifiedFilters.value.delete(filter.key)
        continue
      }
      const raw = Array.isArray(param) ? (param[0] ?? '') : (param ?? '')
      if (filter.type === 'boolean') {
        filter.value = raw === '1' || raw === 'true'
      } else {
        filter.value = raw
      }
      userModifiedFilters.value.add(filter.key)
      if (filter.type === 'date-range' && filter.value) {
        const parts = String(filter.value).split(',')
        if (parts.length === 2) {
          const getTime = (s: string) => s.trim().split(' ')[1] || '00:00'
          dateRangeTimes.value[filter.key] = {
            startTime: getTime(parts[0]),
            endTime: getTime(parts[1])
          }
        }
      }
    }
  }

  const queryMatches = (a: Record<string, string>, b: LocationQuery): boolean => {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)])
    for (const k of keys) {
      const av = a[k]
      const bv = b[k]
      const bStr = bv === undefined || bv === null ? '' : Array.isArray(bv) ? (bv[0] ?? '') : bv
      if (av !== bStr) return false
    }
    return true
  }

  // Used to preserve params that were set by the parent via window.history.replaceState
  const getCurrentUrlQuery = (): LocationQuery => {
    const search = window.location.search
    if (!search || search === '?') return {}
    const params = new URLSearchParams(search)
    const q: LocationQuery = {}
    for (const [k, v] of params.entries()) {
      q[k] = v
    }
    return q
  }

  const pushStateToQuery = () => {
    const q = stateToQuery()
    const tableKeys = new Set([
      'page',
      'search',
      ...(props.filterableFields?.map((f) => `f_${f.key}`) ?? [])
    ])
    const merged: LocationQuery = {}
    const currentQuery = getCurrentUrlQuery()
    for (const [k, v] of Object.entries(currentQuery)) {
      if (!tableKeys.has(k)) merged[k] = v
    }
    Object.assign(merged, q)
    router.replace({ path: route.path, query: merged })
  }

  const updateFilters = (event: Event, key: string, label: string, type: any) => {
    const target = event.target as HTMLInputElement
    const value = type === 'boolean' ? target.checked : target.value
    const filter = filters.value.find((filter) => filter.key === key)
    if (filter) {
      userModifiedFilters.value.add(key)

      // If multi-select, add or remove value from the string list
      if (type === 'multi-select') {
        if (!filter.value) filter.value = ''

        // Handle "__all__" option - toggle all other options
        if (value === '__all__') {
          const allOtherOptions =
            filter.options
              ?.filter((opt: any) => opt.value !== '__all__')
              .map((opt: any) => opt.value) || []

          if (target.checked) {
            // Select all options (excluding __all__ itself)
            filter.value = allOtherOptions.join(',')
          } else {
            // Deselect all
            filter.value = ''
          }
        } else {
          if (target.checked) {
            filter.value = filter.value ? `${filter.value},${value}` : value
          } else {
            filter.value = filter.value
              .replace(new RegExp(`(^|,)${value}(,|$)`, 'g'), ',') // Replace matched value with a comma
              .replace(/^,|,$/g, '') // Remove leading or trailing commas
          }
        }
      } else {
        filter.value = value ? value : undefined
      }
    }
  }

  // Helper to check if all options are selected (for __all__ checkbox state)
  const isAllSelected = (filter: Filter) => {
    if (filter.type !== 'multi-select' || !filter.options) return false
    const allOtherOptions = filter.options.filter((opt: any) => opt.value !== '__all__')
    const selectedValues = filter.value ? filter.value.split(',') : []
    return allOtherOptions.every((opt: any) => selectedValues.includes(opt.value))
  }

  // Helper to check if some (but not all) options are selected (for indeterminate state)
  const isSomeSelected = (filter: Filter) => {
    if (filter.type !== 'multi-select' || !filter.options) return false
    const allOtherOptions = filter.options.filter((opt: any) => opt.value !== '__all__')
    const selectedValues = filter.value ? filter.value.split(',') : []
    const selectedCount = allOtherOptions.filter((opt: any) =>
      selectedValues.includes(opt.value)
    ).length
    return selectedCount > 0 && selectedCount < allOtherOptions.length
  }

  // Helper to get count of selected items for multi-select filters
  const getSelectedCount = (filter: Filter) => {
    if (filter.type !== 'multi-select' || !filter.value) return 0
    return filter.value.split(',').filter((v: string) => v.length > 0).length
  }

  // Helper to get the "All Types" label for multi-select filters
  const getAllTypesLabel = (filter: Filter) => {
    if (!filter.options) return t('SelectAll')
    const allOption = filter.options.find((opt: any) => opt.value === '__all__')
    return allOption?.label || t('SelectAll')
  }

  watch(
    () => props.filterableFields,
    (newFilterableFields) => {
      if (newFilterableFields) {
        filters.value = []
        initFilters()
      }
    },
    { deep: true }
  )

  watch(
    filters,
    async () => {
      // Skip fetching during initialization or when restoring state from URL
      if (isInitializing.value || restoringFromQuery.value) return
      isLoading.value = true
      emit('page-change', 1)
      await fetchData(1).finally(() => {
        isLoading.value = false
      })
    },
    { deep: true }
  )

  // Watch for changes to selectedItems prop
  watch(
    () => props.selectedItems,
    (newSelectedItems) => {
      selectedItems.value = newSelectedItems || []
      selectAll.value = selectedItems.value.length === items.value.length && items.value.length > 0
    },
    { deep: true }
  )

  watch([filters, currentPage], () => pushStateToQuery(), { deep: true })

  watch(totalPages, () => {
    if (totalPages.value >= 1 && currentPage.value > totalPages.value) {
      currentPage.value = totalPages.value
      inputPage.value = String(totalPages.value)
    }
  })

  let searchQuerySyncTimeout: ReturnType<typeof setTimeout> | null = null
  watch(searchTerm, () => {
    if (searchQuerySyncTimeout) clearTimeout(searchQuerySyncTimeout)
    searchQuerySyncTimeout = setTimeout(() => pushStateToQuery(), 400)
  })

  watch(
    () => route.query,
    async (newQuery) => {
      if (queryMatches(stateToQuery(), newQuery)) return
      restoringFromQuery.value = true
      applyQueryToState(newQuery)
      isLoading.value = true
      try {
        await fetchData(currentPage.value)
      } finally {
        isLoading.value = false
        restoringFromQuery.value = false
      }
    },
    { deep: true }
  )

  onMounted(async () => {
    initFilters()

    if (Object.keys(route.query).length > 0) {
      restoringFromQuery.value = true
      applyQueryToState(route.query)
    }

    isLoading.value = true
    await fetchData(currentPage.value).finally(() => {
      isLoading.value = false
      isInitializing.value = false
      restoringFromQuery.value = false
    })
  })

  onUnmounted(() => {
    if (searchQuerySyncTimeout) clearTimeout(searchQuerySyncTimeout)
    if (searchDebounceTimeout) clearTimeout(searchDebounceTimeout)
  })
</script>

<template>
  <div>
    <!-- Responsive search and filters section -->
    <div
      v-if="searchableFields || $slots.actions"
      class="flex flex-wrap items-start gap-2 pt-1"
    >
      <!-- Search Input -->
      <div
        v-if="searchableFields"
        class="min-w-[200px] flex-1"
      >
        <div class="relative w-full">
          <div class="pointer-events-none absolute inset-y-0 start-0 z-20 flex items-center ps-3.5">
            <svg
              class="size-4 flex-shrink-0 text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle
                cx="11"
                cy="11"
                r="8"
              ></circle>
              <path d="m21 21-4.3-4.3"></path>
            </svg>
          </div>
          <input
            type="search"
            data-cy="table-search-input"
            :placeholder="t('Search')"
            class="block w-full rounded-lg border border-gray-200 bg-gray-50 pe-4 ps-11 text-sm text-gray-800
              focus:border-purple-500 focus:ring-purple-500 dark:border-neutral-700 dark:bg-neutral-800
              dark:text-neutral-400 dark:placeholder:text-neutral-500 dark:focus:border-purple-500"
            :value="searchTerm"
            @input="handleSearch"
          />
        </div>
      </div>

      <!-- Filters and Actions -->
      <div
        v-if="filters || $slots.actions"
        class="contents"
      >
        <!-- Filters -->
        <div
          v-if="filters"
          class="contents"
        >
          <div
            v-for="filter in filters.filter((f) => f.type === 'boolean')"
            :key="filter.key"
            class="flex h-10 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5"
          >
            <CheckboxField
              :name="filter.key"
              :label="filter.label"
              :modelValue="!!getFilterValue(filter.key)"
              class="truncate pl-1"
              @update:model-value="
                (value) =>
                  updateFilters(
                    { target: { checked: value } } as unknown as Event,
                    filter.key,
                    filter.label,
                    filter.type
                  )
              "
            />
          </div>

          <Popover
            v-for="filter in filters.filter((f) => f.type !== 'boolean' && f.type !== 'date-range')"
            :key="filter.key"
            class="w-full md:w-auto"
            @update:open="(open) => open && (filterSearchTerm = '')"
          >
            <PopoverTrigger as-child>
              <Button variant="secondary">
                <div class="relative flex items-center gap-1">
                  <span
                    class="text-md flex items-center hover:text-purple-600 focus:text-purple-600 focus:outline-none"
                  >
                    {{ filter.label }}
                  </span>
                  <!-- Count badge for multi-select filters (hidden when all selected) -->
                  <span
                    v-if="filter.type === 'multi-select' && !isAllSelected(filter)"
                    class="inline-flex min-w-[20px] items-center justify-center rounded-[20px] bg-purple-200 px-1 py-0.5
                      text-xs font-semibold text-purple-700"
                  >
                    {{ getSelectedCount(filter) }}
                  </span>
                  <ChevronDownIcon class="h-3 w-3" />
                </div>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              :align="'start'"
              class="w-[220px] rounded-xl p-0 shadow-[0px_4px_8px_rgba(0,0,0,0.08),0px_8px_12px_rgba(0,0,0,0.08)]"
            >
              <Command>
                <!-- Filter Item, depending on type -->
                <CommandList>
                  <CommandInput
                    v-if="filter.type === 'select' || filter.type === 'multi-select'"
                    v-model="filterSearchTerm"
                    :placeholder="t('SearchOptions')"
                    class="my-1 h-9 border-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                  />
                  <CommandGroup>
                    <CommandItem
                      v-if="filter.type == 'number'"
                      :value="filter.key"
                      @select.prevent
                    >
                      <label class="cursor-pointer select-none text-sm font-medium text-gray-700">
                        {{ filter.label }}
                      </label>
                      <input
                        type="number"
                        class="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        :value="getFilterValue(filter.key)"
                        @change="updateFilters($event, filter.key, filter.label, filter.type)"
                      />
                    </CommandItem>
                    <CommandItem
                      v-if="filter.type == 'string'"
                      :value="filter.key"
                      @select.prevent
                    >
                      <label class="cursor-pointer select-none text-sm font-medium text-gray-700">
                        {{ filter.label }}
                      </label>
                      <input
                        type="text"
                        class="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        :value="getFilterValue(filter.key)"
                        @change="updateFilters($event, filter.key, filter.label, filter.type)"
                      />
                    </CommandItem>
                  </CommandGroup>
                  <CommandGroup v-if="filter.type == 'select'">
                    <CommandEmpty v-if="!filter.options?.length">
                      {{ t('NoOptionsFound') }}
                    </CommandEmpty>
                    <CommandItem
                      v-for="option in filter.options?.filter((opt) =>
                        opt.label.toLowerCase().includes(filterSearchTerm?.toLowerCase() || '')
                      )"
                      :key="option.value"
                      :value="option.label || ''"
                      :class="{
                        'selected-option bg-accent': getFilterValue(filter.key) === option.value
                      }"
                      @select="
                        updateFilters(
                          { target: { value: option.value } } as unknown as Event,
                          filter.key,
                          filter.label,
                          filter.type
                        )
                      "
                    >
                      <div class="flex w-full items-center justify-between">
                        <div class="flex items-center gap-2">
                          <span
                            :class="{
                              'text-gray-500': option.value === null,
                              'text-gray-800': option.value !== null
                            }"
                          >
                            {{ option.label }}
                          </span>
                        </div>
                        <CheckIcon
                          v-if="getFilterValue(filter.key) === option.value"
                          class="h-4 w-4 text-gray-600"
                        />
                      </div>
                    </CommandItem>
                  </CommandGroup>
                  <!-- Multi-select options -->
                  <CommandGroup
                    v-if="filter.type == 'multi-select'"
                    class="!p-0"
                  >
                    <!-- "Alle Typen" header -->
                    <div class="border-b border-gray-200 px-3 py-2.5">
                      <label class="flex h-5 cursor-pointer items-center gap-2">
                        <input
                          class="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          type="checkbox"
                          value="__all__"
                          :checked="isAllSelected(filter)"
                          :indeterminate="isSomeSelected(filter)"
                          @change="updateFilters($event, filter.key, filter.label, filter.type)"
                        />
                        <span class="text-sm font-medium leading-5 text-gray-800">
                          {{ getAllTypesLabel(filter) }}
                        </span>
                      </label>
                    </div>
                    <!-- Individual options -->
                    <div class="p-1.5">
                      <CommandEmpty v-if="!filter.options?.length">
                        {{ t('NoOptionsFound') }}
                      </CommandEmpty>
                      <CommandItem
                        v-for="option in filter.options?.filter(
                          (opt) =>
                            opt.value !== '__all__' &&
                            opt.label.toLowerCase().includes(filterSearchTerm?.toLowerCase() || '')
                        )"
                        :key="option.value"
                        class="w-full !rounded-md !px-1.5 !py-1.5"
                        :value="option.value"
                        @select.prevent
                      >
                        <label class="flex h-5 cursor-pointer items-center gap-2">
                          <input
                            class="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            type="checkbox"
                            :value="option.value"
                            :checked="getFilterValue(filter.key)?.includes(option.value)"
                            @change="updateFilters($event, filter.key, filter.label, filter.type)"
                          />
                          <span class="text-sm font-medium leading-5 text-gray-800">
                            {{ option.label }}
                          </span>
                        </label>
                      </CommandItem>
                    </div>
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          <!-- Date Range Filters -->
          <Popover
            v-for="filter in filters.filter((f) => f.type === 'date-range')"
            :key="filter.key"
            class="w-full md:w-auto"
          >
            <PopoverTrigger as-child>
              <Button variant="secondary">
                <div class="relative flex items-center gap-1">
                  <CalendarIcon class="h-4 w-4" />
                  <span
                    class="text-md flex items-center hover:text-purple-600 focus:text-purple-600 focus:outline-none"
                  >
                    {{ filter.label }}
                  </span>
                  <ChevronDownIcon class="h-3 w-3" />
                </div>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              :align="'end'"
              class="w-auto p-0"
            >
              <RangeCalendar
                :model-value="parseDateRangeValue(filter.value)"
                @update:model-value="(range: DateRange) => updateDateRangeFilter(filter.key, range)"
              />
              <div class="flex items-center border-t border-gray-200 px-3 py-2">
                <!-- Quick select buttons (no range selected) -->
                <div
                  v-if="!parseDateRangeValue(filter.value).start"
                  class="grid w-full grid-cols-2 gap-1"
                >
                  <Button
                    variant="outline"
                    size="sm"
                    class="w-full border-purple-200 bg-purple-100 text-xs text-purple-700 hover:bg-purple-200"
                    @click="setTodayRange(filter.key)"
                  >
                    Today
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    class="w-full border-purple-200 bg-purple-100 text-xs text-purple-700 hover:bg-purple-200"
                    @click="setYesterdayRange(filter.key)"
                  >
                    Yesterday
                  </Button>
                </div>
                <!-- Time selectors + clear (range selected) -->
                <div
                  v-else
                  class="flex w-full flex-col gap-2"
                >
                  <div class="flex items-center gap-3">
                    <div class="flex items-center gap-1.5">
                      <label class="text-xs text-gray-500">From</label>
                      <TimePicker
                        :model-value="dateRangeTimes[filter.key]?.startTime || '00:00'"
                        @update:model-value="
                          (t: string) => updateDateRangeTime(filter.key, 'startTime', t)
                        "
                      />
                    </div>
                    <div class="flex items-center gap-1.5">
                      <label class="text-xs text-gray-500">To</label>
                      <TimePicker
                        :model-value="dateRangeTimes[filter.key]?.endTime || '23:59'"
                        @update:model-value="
                          (t: string) => updateDateRangeTime(filter.key, 'endTime', t)
                        "
                      />
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    class="w-full text-xs text-gray-500 hover:text-red-600"
                    @click="removeFilterValue(filter.key)"
                  >
                    Clear filter
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <!-- Actions -->
        <div
          v-if="$slots.actions"
          class="flex items-center gap-x-2"
        >
          <slot name="actions"></slot>
        </div>
      </div>
    </div>

    <!-- Filter Chips -->
    <div class="flex flex-wrap gap-x-2 gap-y-1 pb-4 pt-4">
      <div
        v-for="filter in filters.filter(
          (filter) => filter.value && isFilterValueDifferentFromDefault(filter)
        )"
        :key="filter.key"
        class="flex items-center gap-1 rounded-md bg-purple-100 px-2 py-1 text-sm text-purple-600"
      >
        <span class="font-medium">
          {{ filter.label }}{{ filter.type === 'boolean' && filter.value === true ? '' : ':' }}
        </span>
        <span v-if="filter.type !== 'boolean' || filter.value !== true">
          {{ getFilterDisplayValue(filter.key) }}
        </span>
        <span
          v-if="getFilterValue(filter.key) !== undefined"
          class="cursor-pointer rounded"
          @click.stop="removeFilterValue(filter.key)"
        >
          <XMarkIcon class="h-5 w-5" />
        </span>
      </div>
    </div>

    <!-- Before table slot for banners, alerts, etc -->
    <slot name="before-table"></slot>

    <!-- Improved table container with proper responsive handling -->
    <div class="w-full overflow-hidden">
      <!-- Table with horizontal scroll for mobile -->
      <div class="w-full overflow-x-auto pb-2">
        <div v-if="isLoading">
          <Table>
            <TableHeader>
              <TableRow>
                <TableCell
                  v-for="i in columnCount"
                  :key="i"
                >
                  <SkeletonLoader class="h-4 w-3/4" />
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow
                v-for="i in limit"
                :key="i"
              >
                <TableCell
                  v-for="j in columnCount"
                  :key="j"
                >
                  <SkeletonLoader class="h-4 w-3/4" />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <Table
          v-else-if="items.length > 0"
          :class="[
            'min-w-full divide-y divide-gray-200',
            showColumnDividers &&
              `[&_td]:border-gray-150 [&_th]:border-gray-150 [&_td:last-child]:border-r-0 [&_td]:border-r
              [&_th:last-child]:border-r-0 [&_th]:border-r`
          ]"
        >
          <TableHeader class="w-full bg-gray-50">
            <TableRow>
              <TableCell
                v-if="selectable"
                class="w-12 px-4 py-3.5"
              >
                <input
                  type="checkbox"
                  :checked="selectAll"
                  :indeterminate="selectedItems.length > 0 && selectedItems.length < items.length"
                  class="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  @change="toggleSelectAll"
                />
              </TableCell>
              <slot name="table-head"></slot>
            </TableRow>
          </TableHeader>
          <TableBody>
            <slot
              name="table-row"
              :items="items"
              :toggle-select-item="toggleSelectItem"
              :is-item-selected="isItemSelected"
            ></slot>
          </TableBody>
        </Table>

        <div
          v-else
          class="flex min-h-[300px] flex-col items-center justify-center p-5 text-center md:min-h-[500px]"
        >
          <!-- Custom empty state slot -->
          <slot name="empty">
            <svg
              class="mx-auto mb-4 w-48"
              width="178"
              height="90"
              viewBox="0 0 178 90"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                class="fill-white dark:fill-neutral-800"
                x="27"
                y="50.5"
                width="124"
                height="39"
                rx="7.5"
                fill="currentColor"
              ></rect>
              <rect
                class="stroke-gray-50 dark:stroke-neutral-700/10"
                x="27"
                y="50.5"
                width="124"
                height="39"
                rx="7.5"
                stroke="currentColor"
              ></rect>
              <rect
                class="fill-gray-50 dark:fill-neutral-700/30"
                x="34.5"
                y="58"
                width="24"
                height="24"
                rx="4"
                fill="currentColor"
              ></rect>
              <rect
                class="fill-gray-50 dark:fill-neutral-700/30"
                x="66.5"
                y="61"
                width="60"
                height="6"
                rx="3"
                fill="currentColor"
              ></rect>
              <rect
                class="fill-gray-50 dark:fill-neutral-700/30"
                x="66.5"
                y="73"
                width="77"
                height="6"
                rx="3"
                fill="currentColor"
              ></rect>
              <rect
                class="fill-white dark:fill-neutral-800"
                x="19.5"
                y="28.5"
                width="139"
                height="39"
                rx="7.5"
                fill="currentColor"
              ></rect>
              <rect
                class="stroke-gray-100 dark:stroke-neutral-700/30"
                x="19.5"
                y="28.5"
                width="139"
                height="39"
                rx="7.5"
                stroke="currentColor"
              ></rect>
              <rect
                class="fill-gray-100 dark:fill-neutral-700/70"
                x="27"
                y="36"
                width="24"
                height="24"
                rx="4"
                fill="currentColor"
              ></rect>
              <rect
                class="fill-gray-100 dark:fill-neutral-700/70"
                x="59"
                y="39"
                width="60"
                height="6"
                rx="3"
                fill="currentColor"
              ></rect>
              <rect
                class="fill-gray-100 dark:fill-neutral-700/70"
                x="59"
                y="51"
                width="92"
                height="6"
                rx="3"
                fill="currentColor"
              ></rect>
              <g filter="url(#filter1)">
                <rect
                  class="fill-white dark:fill-neutral-800"
                  x="12"
                  y="6"
                  width="154"
                  height="40"
                  rx="8"
                  fill="currentColor"
                  shape-rendering="crispEdges"
                ></rect>
                <rect
                  class="stroke-gray-100 dark:stroke-neutral-700/60"
                  x="12.5"
                  y="6.5"
                  width="153"
                  height="39"
                  rx="7.5"
                  stroke="currentColor"
                  shape-rendering="crispEdges"
                ></rect>
                <rect
                  class="fill-gray-200 dark:fill-neutral-700"
                  x="20"
                  y="14"
                  width="24"
                  height="24"
                  rx="4"
                  fill="currentColor"
                ></rect>
                <rect
                  class="fill-gray-200 dark:fill-neutral-700"
                  x="52"
                  y="17"
                  width="60"
                  height="6"
                  rx="3"
                  fill="currentColor"
                ></rect>
                <rect
                  class="fill-gray-200 dark:fill-neutral-700"
                  x="52"
                  y="29"
                  width="106"
                  height="6"
                  rx="3"
                  fill="currentColor"
                ></rect>
              </g>
              <defs>
                <filter
                  id="filter1"
                  x="0"
                  y="0"
                  width="178"
                  height="64"
                  filterUnits="userSpaceOnUse"
                  color-interpolation-filters="sRGB"
                >
                  <feFlood
                    flood-opacity="0"
                    result="BackgroundImageFix"
                  ></feFlood>
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  ></feColorMatrix>
                  <feOffset dy="6"></feOffset>
                  <feGaussianBlur stdDeviation="6"></feGaussianBlur>
                  <feComposite
                    in2="hardAlpha"
                    operator="out"
                  ></feComposite>
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.03 0"
                  ></feColorMatrix>
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_1187_14810"
                  ></feBlend>
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_dropShadow_1187_14810"
                    result="shape"
                  ></feBlend>
                </filter>
              </defs>
            </svg>

            <div class="mx-auto max-w-sm">
              <p class="mt-2 font-medium text-gray-800 dark:text-neutral-200">
                {{ t('NoResultsFound') }}
              </p>
              <p class="mb-5 text-sm text-gray-500 dark:text-neutral-500">
                {{ t('NoResultsFoundDescription') }}
              </p>
            </div>
          </slot>
        </div>
      </div>
    </div>

    <!-- Responsive Pagination -->
    <div
      v-if="items.length > 0"
      class="mt-4 flex flex-col-reverse gap-y-3 sm:grid sm:grid-cols-2 sm:items-center sm:gap-x-5"
    >
      <!-- Results Count -->
      <p class="text-sm text-gray-800">
        <span
          data-cy="pagination-results-count"
          class="font-medium"
        >
          {{ paginatedData?.count }}
        </span>
        <span class="ps-2 text-gray-500">{{ t('Results') }}</span>
      </p>

      <!-- Navigation Controls -->
      <nav
        :data-cy="props.paginationNavDataCy"
        class="flex items-center justify-end gap-x-1"
      >
        <button
          :data-cy="props.paginationPrevDataCy"
          class="inline-flex min-h-[38px] min-w-[38px] items-center justify-center gap-x-2 rounded-lg px-2.5 py-2
            text-sm text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none
            disabled:pointer-events-none disabled:opacity-50"
          :disabled="currentPage === 1"
          type="button"
          @click="handlePageChange(currentPage - 1)"
        >
          <ChevronLeftIcon class="mt-0.5 size-3.5 flex-shrink-0" />
          <span
            class="sr-only"
            aria-hidden="true"
          >
            {{ t('Previous') }}
          </span>
        </button>
        <div class="flex items-center gap-x-1">
          <input
            v-model="inputPage"
            :data-cy="props.paginationInputDataCy"
            class="flex h-8 w-12 items-center justify-center rounded-lg border-gray-200 bg-gray-50 px-3 py-2 text-sm
              text-gray-800 ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
            type="text"
            @keyup.enter="goToPage"
          />
          <span
            class="flex min-h-[38px] items-center justify-center px-1.5 py-2 text-sm text-gray-500"
          >
            {{ t('Of') }}
          </span>
          <span
            class="flex min-h-[38px] items-center justify-center px-1.5 py-2 text-sm text-gray-500"
          >
            {{ totalPages }}
          </span>
        </div>
        <button
          :data-cy="props.paginationNextDataCy"
          class="inline-flex min-h-[38px] min-w-[38px] items-center justify-center gap-x-2 rounded-lg px-2.5 py-2
            text-sm text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none
            disabled:pointer-events-none disabled:opacity-50"
          :disabled="!hasNext"
          type="button"
          @click="handlePageChange(currentPage + 1)"
        >
          <span
            class="sr-only"
            aria-hidden="true"
          >
            {{ t('Next') }}
          </span>
          <ChevronRightIcon class="mt-0.5 size-3.5 flex-shrink-0" />
        </button>
      </nav>
    </div>
  </div>
</template>
